import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, ChevronRight, FileText, Utensils, Wine, Beer, GlassWater, Waves, Cookie, Coffee, Sparkles, Sofa, Home, Refrigerator, Flower2, HeartPulse, Compass, Bike, Sunset, ShoppingBag, Store, UtensilsCrossed } from "lucide-react";
import { LegalLinks } from "../components/legal/LegalBits";
import { fetchRestaurant, fetchMenus, track } from "../lib/api";
import { T, G, tr } from "../lib/i18n";
import { RestaurantHeader } from "../components/RestaurantHeader";
import { QRDialog } from "../components/QRDialog";
import { ExternalReviewsBadges } from "../components/ExternalReviewsBadges";
import AppBottomBar from "../components/AppBottomBar";
import ActiveExperienceBanner from "../components/ActiveExperienceBanner";
import { Toaster } from "../components/ui/toaster";
import { Star } from "lucide-react";

// Custom champagne flute icon with rising bubbles (Lucide has no bubbly flute)
function ChampagneFlute({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      {/* flute bowl */}
      <path d="M9 3h6l-.7 8.2a2.3 2.3 0 0 1-2.3 2.1h0a2.3 2.3 0 0 1-2.3-2.1L9 3z" />
      {/* stem */}
      <path d="M12 13.3V20" />
      {/* base */}
      <path d="M9.5 20h5" />
      {/* bubbles rising inside bowl */}
      <circle cx="11" cy="10.5" r="0.55" fill="currentColor" />
      <circle cx="13" cy="8.5"  r="0.55" fill="currentColor" />
      <circle cx="11.6" cy="6.5" r="0.5" fill="currentColor" />
      <circle cx="12.6" cy="4.6" r="0.4" fill="currentColor" />
    </svg>
  );
}

const ICONS = {
  utensils: Utensils,
  wine: Wine,
  beer: Beer,
  "glass-water": GlassWater,
  waves: Waves,
  cookie: Cookie,
  coffee: Coffee,
  sparkles: Sparkles,
  sofa: Sofa,
  home: Home,
  refrigerator: Refrigerator,
  champagne: ChampagneFlute,
  spa: Flower2,
  flower: HeartPulse,
};

// Check if a menu is available now based on availability field
function isAvailableNow(menu) {
  const avail = menu.availability || "always";
  if (avail === "always") return true;
  const now = new Date();
  const mins = now.getHours() * 60 + now.getMinutes();
  const toMin = (hm) => {
    if (!hm || !/^\d{1,2}:\d{2}$/.test(hm)) return null;
    const [h, m] = hm.split(":").map(Number);
    return h * 60 + m;
  };
  const RANGES = {
    breakfast: [7 * 60, 10 * 60 + 30],
    lunch: [12 * 60, 15 * 60],
    dinner: [19 * 60, 22 * 60 + 30],
  };
  let start, end;
  if (avail === "custom") {
    start = toMin(menu.time_start); end = toMin(menu.time_end);
  } else if (RANGES[avail]) {
    [start, end] = RANGES[avail];
  }
  if (start == null || end == null) return true;
  if (start <= end) return mins >= start && mins <= end;
  // range wraps midnight
  return mins >= start || mins <= end;
}

export default function MenuIndexPage() {
  const navigate = useNavigate();
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || "it");
  const [restaurant, setRestaurant] = useState(null);
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);

  useEffect(() => { localStorage.setItem("lang", lang); track({ type: "lang_change", lang }); }, [lang]);

  useEffect(() => { track({ type: "home_view", lang }); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [r, m] = await Promise.all([fetchRestaurant(), fetchMenus()]);
        if (!alive) return;
        setRestaurant(r);
        setMenus(m);
      } catch (e) {
        setError(e?.response?.data?.detail || e.message || "Errore");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f7ebdf]">
        <div className="flex flex-col items-center gap-3 text-[#4a1c17]">
          <Loader2 className="h-8 w-8 animate-spin" />
          <p className="text-sm">{tr(G.loadingMenu, lang)}</p>
        </div>
      </div>
    );
  }
  if (error || !restaurant) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f7ebdf]">
        <div className="text-center max-w-sm">
          <p className="font-serif text-xl text-[#4a1c17]">{tr(G.loadError, lang)}</p>
          <p className="mt-2 text-sm text-[#7c5a4e]">{error || tr(G.noData, lang)}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7ebdf]">
      <RestaurantHeader
        restaurant={restaurant}
        lang={lang}
        setLang={setLang}
        onOpenQR={() => setQrOpen(true)}
        collapseOnScroll
        primaryAction={{
          label: tr({ it: "Tavolo", en: "Table", es: "Mesa", fr: "Table", de: "Tisch" }, lang),
          title: tr({ it: "Prenota un tavolo", en: "Book a table", es: "Reservar mesa", fr: "Réserver une table", de: "Tisch reservieren" }, lang),
          icon: <UtensilsCrossed className="h-4 w-4" />,
          testId: "header-book-table-btn",
          highlight: true,
          onClick: () => { track({ type: "prenota_view", lang }); navigate("/prenota?ref=app"); },
        }}
        leftAction={(() => {
          const m = menus.find((x) => x.icon === "spa" || x.link === "/spa");
          if (!m) return null;
          const Icon = ICONS[m.icon] || Utensils;
          return {
            label: tr(m.name, lang),
            icon: <Icon className="h-4 w-4" />,
            testId: "header-spa-btn",
            onClick: () => {
              track({ type: "spa_view", lang });
              navigate(m.link || "/spa");
            },
          };
        })()}
        rightAction={(() => {
          const m = menus.find((x) => x.icon === "flower" || x.link === "/massaggi");
          if (!m) return null;
          const Icon = ICONS[m.icon] || Utensils;
          return {
            label: tr(m.name, lang),
            icon: <Icon className="h-4 w-4" />,
            testId: "header-massage-btn",
            onClick: () => navigate(m.link || "/massaggi"),
          };
        })()}
        extraRightAction={{
          label: tr({ it: "Dintorni", en: "Nearby", es: "Alrededores", fr: "Alentours", de: "In der Nähe" }, lang),
          icon: <Compass className="h-4 w-4" />,
          testId: "header-dintorni-btn",
          onClick: () => navigate("/dintorni"),
        }}
        secondaryAction={{
          label: "Bici",
          icon: <Bike className="h-4 w-4" />,
          testId: "header-bikesquare-btn",
          title: tr({ it: "Noleggio biciclette", en: "Bike rental", es: "Alquiler bicicletas", fr: "Location vélos", de: "Fahrradverleih" }, lang),
          onClick: () => navigate("/bici"),
        }}
        tertiaryAction={{
          label: tr({ it: "Cavallo", en: "Horse Ride", es: "A Caballo", fr: "À Cheval", de: "Ausritt" }, lang),
          icon: <Sunset className="h-4 w-4" />,
          testId: "header-cavallo-btn",
          title: tr({ it: "Giro a cavallo al tramonto", en: "Sunset horse ride", es: "Paseo a caballo al atardecer", fr: "Balade à cheval au coucher", de: "Sonnenuntergang-Ausritt" }, lang),
          onClick: () => navigate("/cavallo"),
        }}
      />

      <main className="max-w-3xl mx-auto px-4 pt-4 pb-32">
        <ActiveExperienceBanner lang={lang} />
        <div className="mb-3 px-1">
          <h2 className="text-lg sm:text-xl font-bold text-[#2b1410] tracking-tight">
            {tr({ it:"I nostri menu", en:"Our menus", es:"Nuestros menús", fr:"Nos menus", de:"Unsere Menüs" }, lang)}
          </h2>
          <p className="mt-0.5 text-sm text-[#7c5a4e] italic">
            {tr({ it:"Scegli un menu per iniziare", en:"Choose a menu to start", es:"Elige un menú", fr:"Choisis un menu", de:"Wähle ein Menü" }, lang)}
          </p>
        </div>

        <ul className="space-y-3">
          {menus
            .filter(isAvailableNow)
            .filter((m) => !(m.icon === "spa" || m.link === "/spa" || m.icon === "flower" || m.link === "/massaggi"))
            .map((m) => {
            const Icon = ICONS[m.icon] || Utensils;
            const hasCover = m.image && m.image.length > 0;
            const highlight = m.highlight === true || m.icon === "home";
            return (
              <li key={m.id}>
                <button
                  onClick={() => {
                    if (m.link || m.icon === "spa") {
                      track({ type: "spa_view", lang });
                      navigate(m.link || "/spa");
                    } else {
                      track({ type: "menu_view", lang, menu_id: m.id });
                      navigate(`/menu/${m.id}`);
                    }
                  }}
                  data-testid={`menu-card-${m.id}`}
                  className={
                    highlight
                      ? "group w-full relative flex items-center gap-4 bg-[#4a1c17] hover:bg-[#3a1611] border border-[#4a1c17] rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all text-left"
                      : "group w-full relative flex items-center gap-4 bg-white hover:bg-white border border-[#e8dcc8] hover:border-[#4a1c17]/40 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all text-left"
                  }
                >
                  {hasCover ? (
                    <div className="relative w-24 sm:w-32 h-24 sm:h-28 shrink-0 overflow-hidden">
                      <img src={m.image} alt="" className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className={highlight ? "absolute inset-0 bg-gradient-to-r from-transparent to-[#4a1c17]/50" : "absolute inset-0 bg-gradient-to-r from-transparent to-white/40"} />
                    </div>
                  ) : (
                    <div className="px-4 py-4 sm:px-5 sm:py-5">
                      <div className={
                        highlight
                          ? "h-12 w-12 sm:h-14 sm:w-14 rounded-xl bg-white/15 text-[#f7ebdf] flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform"
                          : "h-12 w-12 sm:h-14 sm:w-14 rounded-xl bg-[#4a1c17] text-[#f7ebdf] flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform"
                      }>
                        <Icon className="h-6 w-6 sm:h-7 sm:w-7" />
                      </div>
                    </div>
                  )}
                  <div className="flex-1 min-w-0 py-4 pr-4 sm:pr-5">
                    <div className={
                      highlight
                        ? "text-xl sm:text-2xl font-extrabold tracking-tight text-white"
                        : "text-xl sm:text-2xl font-extrabold tracking-tight text-[#2b1410]"
                    }>
                      {tr(m.name, lang)}
                    </div>
                    {tr(m.subtitle, lang) && (
                      <div className={
                        highlight
                          ? "mt-1 text-sm sm:text-base font-medium text-white/80 truncate"
                          : "mt-1 text-sm sm:text-base font-medium text-[#7c5a4e] truncate"
                      }>
                        {tr(m.subtitle, lang)}
                      </div>
                    )}
                  </div>
                  <ChevronRight className={
                    highlight
                      ? "h-5 w-5 mr-4 sm:mr-5 text-white/70 group-hover:text-white group-hover:translate-x-0.5 transition-all shrink-0"
                      : "h-5 w-5 mr-4 sm:mr-5 text-[#7c5a4e] group-hover:text-[#4a1c17] group-hover:translate-x-0.5 transition-all shrink-0"
                  } />
                </button>
              </li>
            );
          })}
        </ul>

        {menus.length === 0 && (
          <div className="py-16 text-center text-[#7c5a4e]">
            <p>{tr(G.noMenus, lang)}</p>
          </div>
        )}

        <ExternalReviewsBadges platforms={restaurant?.externalReviews || []} lang={lang} />

        {/* Social footer — a subtle, always-visible entry point to /social */}
        <div className="mt-10 rounded-2xl bg-gradient-to-br from-[#4a1c17] to-[#2b1410] text-[#f7ebdf] p-5 sm:p-6 relative overflow-hidden">
          <div className="absolute -top-8 -right-8 opacity-10">
            <Sparkles className="h-32 w-32" />
          </div>
          <div className="relative flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-1">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-[#c48b31]">
                {tr({ it: "Ci trovi qui", en: "Find us here", es: "Aquí estamos", fr: "Retrouve-nous ici", de: "Hier findest du uns" }, lang)}
              </div>
              <h3 className="mt-1 font-serif text-xl sm:text-2xl font-bold">
                {tr({ it: "Seguici sui social", en: "Follow us on social", es: "Síguenos en redes", fr: "Suivez-nous", de: "Folge uns" }, lang)}
              </h3>
              <p className="mt-1 text-sm opacity-85">
                {tr({
                  it: "Reels, ricette, borghi, dietro le quinte. Ogni settimana.",
                  en: "Reels, recipes, villages, behind the scenes. Every week.",
                  es: "Reels, recetas, pueblos, tras las escenas. Cada semana.",
                  fr: "Reels, recettes, villages, coulisses. Chaque semaine.",
                  de: "Reels, Rezepte, Dörfer, Kulissen. Jede Woche.",
                }, lang)}
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate("/social")}
              data-testid="home-social-cta"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#c48b31] hover:bg-[#a56c1e] text-white px-5 py-2.5 text-sm font-semibold transition-colors shrink-0"
            >
              <Sparkles className="h-4 w-4" />
              {tr({ it: "Seguici", en: "Follow", es: "Seguir", fr: "Suivre", de: "Folgen" }, lang)}
            </button>
          </div>
        </div>

        <Link to="/preventivo" className="mt-8 block rounded-2xl bg-[#4a1c17] text-[#f7ebdf] p-5 shadow-md hover:bg-[#2b1410] transition" data-testid="home-quote-cta">
          <div className="flex items-center gap-4">
            <div className="h-11 w-11 rounded-full bg-white/10 flex items-center justify-center shrink-0"><FileText className="h-5 w-5 text-[#e6c46a]" /></div>
            <div className="flex-1 min-w-0">
              <div className="font-serif text-lg font-bold leading-tight">{tr({ it: "Richiedi un preventivo", en: "Request a quote", es: "Pide un presupuesto", fr: "Demander un devis", de: "Angebot anfragen" }, lang)}</div>
              <div className="text-xs text-[#f7ebdf]/80">{tr({ it: "Soggiorni, cerimonie, gruppi e feste su misura — ti rispondiamo in poche ore", en: "Stays, ceremonies, groups and parties — tailored, within hours", es: "Estancias, ceremonias, grupos y fiestas a medida", fr: "Séjours, cérémonies, groupes et fêtes sur mesure", de: "Aufenthalte, Feiern, Gruppen – maßgeschneidert" }, lang)}</div>
            </div>
            <ChevronRight className="h-5 w-5 text-[#e6c46a]" />
          </div>
        </Link>

        <footer className="mt-8 pt-6 border-t border-[#e8dcc8] text-center">
          <p className="text-xs text-[#7c5a4e]">{tr(T.madeIn, lang)} • {restaurant.name}</p>
          <LegalLinks className="mt-2" />
        </footer>
      </main>

      {/* Bottom bar */}
      <AppBottomBar spacer={false} current="menu" lang={lang} onOpenQR={() => setQrOpen(true)} />

      <QRDialog open={qrOpen} onOpenChange={setQrOpen} lang={lang} restaurant={restaurant} />
      <Toaster />
    </div>
  );
}


